import React, { useState, useEffect, useMemo } from "react";
import HeroSection from "../../layout/HeroSection";
import adjustmentOfStatusData from "../data/adjustmentOfStatusData";
import HelpSection from "../section/HelpSection";
import EligibilityAdjustment from "../section/EligibilityAdjustment";
import PotentialRoadblocks from "../section/PotentialRoadblocks";
import UscisSection from "../section/UscisSection";
import SupportHelp from "../section/SupportHelp";
import api from "../../utils/api";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function AdjustmentOfStatusContent({ currentPage = "home" }) {
    const [pageData, setPageData] = useState(adjustmentOfStatusData[currentPage] || adjustmentOfStatusData["adjustment-status"]);
    const [loading, setLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const { i18n } = useTranslation();

    const staticItems = [
        "Personalized instructions & checklist",
        "Live expert support",
        "Guaranteed approval or money back"
    ];

    useEffect(() => {
        const fetchPackageData = async () => {
            if (currentPage === "adjustment-status" || currentPage === "home") return;

            setLoading(true);
            try {
                const response = await api.get("/form-packages");
                const allPackages = response.data.data;

                // البحث عن البكج المناسب بناءً على الـ slug أو كود الفورم
                const dynamicPackage = allPackages.find(pkg =>
                    pkg.slug === currentPage ||
                    pkg.forms.some(f => f.code.toLowerCase().replace(/\s+/g, '-') === currentPage.toLowerCase())
                );

                if (dynamicPackage) {
                    setPageData(prev => ({
                        ...prev,
                        id: dynamicPackage.id,
                        price: `$${parseFloat(dynamicPackage.price).toFixed(2)}`,
                        savings: `Save $${parseFloat(dynamicPackage.saving_price).toFixed(0)} on average vs attorney`,
                        packageName: dynamicPackage.name,
                        forms: [
                            ...dynamicPackage.forms.map(f => `${f.code}: ${f.name}`),
                            ...staticItems
                        ]
                    }));
                }
            } catch (error) {
                console.error("Error fetching package data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPackageData();
    }, [currentPage]);

    const handleCheckout = async () => {
        if (!isAuthenticated) {
            Swal.fire({
                title: "Login Required",
                text: "Please sign in to your account to purchase this package.",
                icon: "info",
                showCancelButton: true,
                confirmButtonText: "Login Now",
                confirmButtonColor: "#2563eb",
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate(`/${i18n.language}/login`);
                }
            });
            return;
        }

        if (!pageData.id) {
            Swal.fire("Error", "Package information is missing. Please try again later.", "error");
            return;
        }

        setIsProcessing(true);
        try {
            const response = await api.post(`/payments/package/${pageData.id}/create-checkout`);
            if (response.data.success && response.data.data.checkout_url) {
                window.location.href = response.data.data.checkout_url;
            } else {
                Swal.fire("Error", response.data.message || "Failed to initiate payment.", "error");
            }
        } catch (error) {
            console.error("Checkout error:", error);
            const message = error.response?.data?.message || "Something went wrong. Please try again.";
            if (message.toLowerCase().includes("already own")) {
                Swal.fire("Notice", message, "warning");
            } else {
                Swal.fire("Payment Error", message, "error");
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const page = pageData;

    const contentHtml = useMemo(() => {
        if (!page.content) return null;
        return page.content.replace(/href="\/(?!http)/g, `href="/${i18n.language}/`);
    }, [page.content, i18n.language]);

    return (
        <>
            <HeroSection
                badge={page.hero.badge}
                title={page.hero.title}
                subtitle={page.hero.subtitle}
            />

            <UscisSection />
            <SupportHelp />

            {page.content && (
                <section className="py-20 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-6">
                        <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
                    </div>
                </section>
            )}

            {loading ? (
                <div className="py-16 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-600">Loading package details...</p>
                </div>
            ) : (
                page.price && (
                    <section className="py-16 bg-gray-100">
                        <div className="max-w-6xl mx-auto px-6">
                            <h2 className="text-3xl md:text-4xl font-bold text-center text-blue-600 mb-12">
                                Our price is a fraction of what you would pay a lawyer
                            </h2>

                            <div className="grid md:grid-cols-2 gap-12 items-center">
                                <div>
                                    <p className="text-gray-700 mb-6 text-lg">
                                        When you purchase the <strong>{page.packageName}</strong>, you will receive:
                                    </p>

                                    <ul className="space-y-4">
                                        {page.forms.map((item, index) => (
                                            <li key={index} className="flex items-start gap-3">
                                                <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mt-0.5">
                                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                                <span className="text-gray-800 text-lg">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="text-center">
                                    <div className="mb-4">
                                        <span className="text-7xl md:text-8xl font-bold text-gray-900">{page.price}</span>
                                        <span className="text-xl text-gray-600 ml-2">+USCIS fees</span>
                                    </div>
                                    <p className="text-gray-600 text-lg mt-6">
                                        {page.savings}
                                    </p>
                                    <div className="mt-10">
                                        <button
                                            onClick={handleCheckout}
                                            disabled={isProcessing}
                                            className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold text-xl px-20 py-5 rounded-xl transition-all shadow-lg hover:shadow-xl active:scale-95 disabled:bg-gray-400"
                                        >
                                            {isProcessing ? "Processing..." : "Get Started Now"}
                                        </button>
                                        <p className="text-sm text-gray-500 mt-3 flex items-center justify-center gap-2">
                                            <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                            </svg>
                                            Secure Checkout
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )
            )}

            <EligibilityAdjustment />
            <PotentialRoadblocks />
            <HelpSection />
        </>
    );
}
